import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { trackDownload } from '../../../activity';

/**
 * Safely parse date strings (supports YYYYMMDD and ISO formats)
 */
const parseDateString = (dateStr: string) => {
    if (!dateStr) return new Date();
    if (/^\d{8}$/.test(dateStr)) {
        const y = parseInt(dateStr.substring(0, 4), 10);
        const m = parseInt(dateStr.substring(4, 6), 10) - 1;
        const d = parseInt(dateStr.substring(6, 8), 10);
        return new Date(y, m, d);
    }
    return new Date(dateStr);
};

/**
 * Format number with thousand separator
 */
const formatNumber = (num: number) => {
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(num);
};

/**
 * Format decimal with 2 decimal places
 */
const formatDecimal = (num?: number | null) => {
    if (num === undefined || num === null) return '-';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
};

interface BkmMuatRow {
    spb_date: string;
    rkh_number?: string;
    estate_name?: string;
    division_name?: string;
    cost_activity_id: number;
    cost_activity_name: string;
    cost_center_id: number;
    cost_center_name: string;
    loader_id: number;
    loader_name: string;
    sum_bunch_count: number;
    sum_real_weight: number;
    sum_hk: number;
    sum_premi_amount: number;
}

interface BkmMuatParams {
    posting_date: string;
    division_name?: string;
    estate_name?: string;
    rkh_number?: string;
}

const drawHeader = (doc: jsPDF, params: BkmMuatParams, pageNumber: number, totalPages: number) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };
    
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    
    // Header title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('LAPORAN BKM MUAT', margins.left, margins.top + 5);
    
    // Metadata Section
    let leftMetaY = margins.top + 12;
    const metaLineHeight = 4;
    const labelX = margins.left;
    const valueX = margins.left + 25;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    if (params.estate_name) {
        doc.text('Estate', labelX, leftMetaY);
        doc.text(`: ${params.estate_name}`, valueX, leftMetaY);
        leftMetaY += metaLineHeight;
    }

    if (params.division_name) {
        doc.text('Divisi', labelX, leftMetaY);
        doc.text(`: ${params.division_name}`, valueX, leftMetaY);
        leftMetaY += metaLineHeight;
    }

    if (params.rkh_number) {
        doc.text('No. RKH', labelX, leftMetaY);
        doc.text(`: ${params.rkh_number}`, valueX, leftMetaY);
        leftMetaY += metaLineHeight;
    }

    doc.text('Tanggal Post', labelX, leftMetaY);
    doc.text(`: ${format(parseDateString(params.posting_date), 'dd-MM-yyyy')}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;

    doc.text('Tanggal Cetak', labelX, leftMetaY);
    doc.text(`: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, valueX, leftMetaY);
    leftMetaY += metaLineHeight;
    
    doc.text('Halaman', labelX, leftMetaY);
    doc.text(`: ${pageNumber} / ${totalPages}`, valueX, leftMetaY);

    // Signature section (Top Right)
    const sigY = margins.top + 5;
    const totalSigWidth = 90;
    const sigBoxWidth = totalSigWidth / 3;
    const sigBoxHeight = 26; 
    const sigStartX = pageWidth - margins.right - totalSigWidth;
    
    const sigs = [
        'DIPERIKSA OLEH\nMANDOR',
        'DIPERIKSA OLEH\nASISTEN',
        'DISETUJUI OLEH\nMANAGER'
    ];

    doc.setFontSize(6);
    
    sigs.forEach((title, i) => {
        const x = sigStartX + (i * sigBoxWidth);
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        doc.rect(x, sigY, sigBoxWidth, sigBoxHeight);
        
        const titleLines = title.split('\n');
        const statusText = titleLines[0];
        const roleText = titleLines.slice(1).join('\n');

        doc.setFont('helvetica', 'normal');
        doc.text(statusText, x + (sigBoxWidth / 2), sigY + 3, { align: 'center' });
        
        const statusLineY = sigY + 4.5;
        doc.line(x, statusLineY, x + sigBoxWidth, statusLineY);

        doc.text(roleText, x + (sigBoxWidth / 2), statusLineY + 3.5, { align: 'center', maxWidth: sigBoxWidth - 2 });

        const rowHeight = 4;
        const lineTglY = sigY + sigBoxHeight - rowHeight;
        doc.line(x, lineTglY, x + sigBoxWidth, lineTglY);

        doc.setFontSize(5);
        doc.text('Nama', x + 1, lineTglY - 1); 
        doc.text('Tgl', x + 1, lineTglY + 3);
        doc.setFontSize(6); 
    });
};

export const generateBkmMuatPDF = (data: BkmMuatRow[], params: BkmMuatParams, rkhBlocks: any[] = []) => {
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };
    
    const tableBody: any[] = [];
    let globalIndex = 1;

    // Grouping logic for 3 levels: Cost Activity -> Cost Center -> Loader
    // Since the API returns sorted/grouped rows already, we can nest them
    const groupedData: {
        [activity: string]: {
            [costCenter: string]: BkmMuatRow[]
        }
    } = {};

    data.forEach((row) => {
        const actName = row.cost_activity_name || '<Tanpa Aktivitas>';
        const ccName = row.cost_center_name || '<Tanpa Cost Center>';
        
        if (!groupedData[actName]) {
            groupedData[actName] = {};
        }
        if (!groupedData[actName][ccName]) {
            groupedData[actName][ccName] = [];
        }
        groupedData[actName][ccName].push(row);
    });

    // Subtotal variables
    let grandTotalTandan = 0;
    let grandTotalHasil = 0;
    let grandTotalHk = 0;
    let grandTotalPremi = 0;

    const getBlockTarget = (ccName: string) => {
        const cleanedCc = ccName.trim();
        const block = rkhBlocks.find((b: any) => b.block_code?.trim() === cleanedCc);
        return {
            est_bunch: block ? block.est_bunch : 0,
            est_weight: block ? block.est_weight : 0
        };
    };

    Object.keys(groupedData).forEach((actName) => {
        // Calculate Activity Totals and Targets
        let actBunch = 0;
        let actWeight = 0;
        let actHk = 0;
        let actPremi = 0;
        let actTargetBunch = 0;
        let actTargetWeight = 0;
        
        const costCenters = groupedData[actName];
        Object.keys(costCenters).forEach((ccName) => {
            costCenters[ccName].forEach((loader) => {
                actBunch += loader.sum_bunch_count || 0;
                actWeight += loader.sum_real_weight || 0;
                actHk += loader.sum_hk || 0;
                actPremi += loader.sum_premi_amount || 0;
            });
            const ccTarget = getBlockTarget(ccName);
            actTargetBunch += ccTarget.est_bunch;
            actTargetWeight += ccTarget.est_weight;
        });

        // Level 1: Activity Row with sums and targets
        tableBody.push([
            '',
            { content: actName, styles: { fontStyle: 'bold', fillColor: [240, 240, 240], halign: 'left' } },
            actTargetBunch > 0 ? formatNumber(actTargetBunch) : '-',
            { content: formatNumber(actBunch), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            actTargetWeight > 0 ? formatDecimal(actTargetWeight) : '-',
            { content: formatDecimal(actWeight), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: formatDecimal(actHk), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: formatDecimal(actPremi), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
        ]);

        Object.keys(costCenters).forEach((ccName) => {
            // Calculate Cost Center Totals
            let ccBunch = 0;
            let ccWeight = 0;
            let ccHk = 0;
            let ccPremi = 0;

            const loaders = costCenters[ccName];
            loaders.forEach((loader) => {
                ccBunch += loader.sum_bunch_count || 0;
                ccWeight += loader.sum_real_weight || 0;
                ccHk += loader.sum_hk || 0;
                ccPremi += loader.sum_premi_amount || 0;
            });

            const ccTarget = getBlockTarget(ccName);

            // Level 2: Cost Center Row with sums and targets
            tableBody.push([
                '',
                { content: `   ${ccName}`, styles: { fontStyle: 'bold', halign: 'left', fillColor: [250, 250, 250] } },
                ccTarget.est_bunch > 0 ? formatNumber(ccTarget.est_bunch) : '-',
                { content: formatNumber(ccBunch), styles: { fontStyle: 'bold', fillColor: [250, 250, 250] } },
                ccTarget.est_weight > 0 ? formatDecimal(ccTarget.est_weight) : '-',
                { content: formatDecimal(ccWeight), styles: { fontStyle: 'bold', fillColor: [250, 250, 250] } },
                { content: formatDecimal(ccHk), styles: { fontStyle: 'bold', fillColor: [250, 250, 250] } },
                { content: formatDecimal(ccPremi), styles: { fontStyle: 'bold', fillColor: [250, 250, 250] } }
            ]);

            loaders.forEach((loader) => {
                grandTotalTandan += loader.sum_bunch_count || 0;
                grandTotalHasil += loader.sum_real_weight || 0;
                grandTotalHk += loader.sum_hk || 0;
                grandTotalPremi += loader.sum_premi_amount || 0;

                // Level 3: Loader Row
                tableBody.push([
                    globalIndex++,
                    `      ${loader.loader_name || '-'}`,
                    '-', // Tandan Target
                    formatNumber(loader.sum_bunch_count), // Tandan Hasil
                    '-', // Weight Target
                    formatDecimal(loader.sum_real_weight), // Weight Hasil
                    formatDecimal(loader.sum_hk), // HK
                    formatDecimal(loader.sum_premi_amount) // Premi
                ]);
            });
        });
    });

    // Add Grand Total Row
    tableBody.push([
        '',
        { content: 'TOTAL', styles: { fontStyle: 'bold', halign: 'left' } },
        '',
        { content: formatNumber(grandTotalTandan), styles: { fontStyle: 'bold' } },
        '',
        { content: formatDecimal(grandTotalHasil), styles: { fontStyle: 'bold' } },
        { content: formatDecimal(grandTotalHk), styles: { fontStyle: 'bold' } },
        { content: formatDecimal(grandTotalPremi), styles: { fontStyle: 'bold' } }
    ]);

    // Group records by loader for Muat and Langsir separately
    const muatLoaders: { [id: number]: { name: string; bunch: number; weight: number; hk: number; premi: number } } = {};
    const langsirMovers: { [id: number]: { name: string; bunch: number; weight: number; hk: number; premi: number } } = {};

    data.forEach((row) => {
        const isLangsir = (row.cost_activity_name || '').toLowerCase().includes('langsir');
        const targetGroup = isLangsir ? langsirMovers : muatLoaders;
        
        const loaderId = row.loader_id || 0;
        if (!targetGroup[loaderId]) {
            targetGroup[loaderId] = {
                name: row.loader_name || '-',
                bunch: 0,
                weight: 0,
                hk: 0,
                premi: 0
            };
        }
        
        targetGroup[loaderId].bunch += row.sum_bunch_count || 0;
        targetGroup[loaderId].weight += row.sum_real_weight || 0;
        targetGroup[loaderId].hk += row.sum_hk || 0;
        targetGroup[loaderId].premi += row.sum_premi_amount || 0;
    });

    const muatSummaryBody: any[] = [];
    let muatTotalBunch = 0;
    let muatTotalWeight = 0;
    let muatTotalHk = 0;
    let muatTotalPremi = 0;
    let idxMuat = 1;

    Object.keys(muatLoaders).forEach((key) => {
        const item = muatLoaders[Number(key)];
        muatTotalBunch += item.bunch;
        muatTotalWeight += item.weight;
        muatTotalHk += item.hk;
        muatTotalPremi += item.premi;

        muatSummaryBody.push([
            idxMuat++,
            item.name,
            formatNumber(item.bunch),
            formatDecimal(item.weight),
            formatDecimal(item.hk),
            formatDecimal(item.premi)
        ]);
    });

    if (muatSummaryBody.length > 0) {
        muatSummaryBody.push([
            '',
            { content: 'TOTAL', styles: { fontStyle: 'bold', halign: 'left' } },
            { content: formatNumber(muatTotalBunch), styles: { fontStyle: 'bold' } },
            { content: formatDecimal(muatTotalWeight), styles: { fontStyle: 'bold' } },
            { content: formatDecimal(muatTotalHk), styles: { fontStyle: 'bold' } },
            { content: formatDecimal(muatTotalPremi), styles: { fontStyle: 'bold' } }
        ]);
    }

    const langsirSummaryBody: any[] = [];
    let langsirTotalBunch = 0;
    let langsirTotalWeight = 0;
    let langsirTotalHk = 0;
    let langsirTotalPremi = 0;
    let idxLangsir = 1;

    Object.keys(langsirMovers).forEach((key) => {
        const item = langsirMovers[Number(key)];
        langsirTotalBunch += item.bunch;
        langsirTotalWeight += item.weight;
        langsirTotalHk += item.hk;
        langsirTotalPremi += item.premi;

        langsirSummaryBody.push([
            idxLangsir++,
            item.name,
            formatNumber(item.bunch),
            formatDecimal(item.weight),
            formatDecimal(item.hk),
            formatDecimal(item.premi)
        ]);
    });

    if (langsirSummaryBody.length > 0) {
        langsirSummaryBody.push([
            '',
            { content: 'TOTAL', styles: { fontStyle: 'bold', halign: 'left' } },
            { content: formatNumber(langsirTotalBunch), styles: { fontStyle: 'bold' } },
            { content: formatDecimal(langsirTotalWeight), styles: { fontStyle: 'bold' } },
            { content: formatDecimal(langsirTotalHk), styles: { fontStyle: 'bold' } },
            { content: formatDecimal(langsirTotalPremi), styles: { fontStyle: 'bold' } }
        ]);
    }

    // Table widths (A4 width is 210mm, usable width is 190mm)
    const wNo = 10;
    const wDesc = 70;
    const wTandanTarget = 15;
    const wTandanHasil = 20;
    const wWeightTarget = 15;
    const wWeightHasil = 20;
    const wHk = 20;
    const wPremi = 20;

    autoTable(doc, {
        startY: 52,
        margin: { top: 52, bottom: 20, left: margins.left },
        head: [
            [
                { content: 'NO', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: 'KEGIATAN / LOKASI / PEKERJA', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: 'TANDAN', colSpan: 2, styles: { halign: 'center' } },
                { content: 'BERAT (KG)', colSpan: 2, styles: { halign: 'center' } },
                { content: 'HK', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: 'PREMI', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }
            ],
            [
                'TARGET', 'HASIL',
                'TARGET', 'HASIL'
            ]
        ],
        body: tableBody,
        theme: 'grid',
        styles: {
            fontSize: 7.5,
            cellPadding: 1.5,
            halign: 'center',
            valign: 'middle',
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            textColor: [0, 0, 0]
        },
        headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineWidth: 0.1,
            fontSize: 8,
            cellPadding: 1
        },
        columnStyles: {
            0: { halign: 'right', cellWidth: wNo },
            1: { halign: 'left', cellWidth: wDesc },
            2: { halign: 'center', cellWidth: wTandanTarget },
            3: { halign: 'right', cellWidth: wTandanHasil },
            4: { halign: 'center', cellWidth: wWeightTarget },
            5: { halign: 'right', cellWidth: wWeightHasil },
            6: { halign: 'right', cellWidth: wHk },
            7: { halign: 'right', cellWidth: wPremi }
        },
        didParseCell: (data) => {
            if (data.row.section === 'body') {
                const rawRow = data.row.raw as any;
                if (rawRow) {
                    const descCell = rawRow[1];
                    const descText = descCell && typeof descCell === 'object' ? descCell.content : String(descCell);
                    
                    // Grand Total Row
                    if (descText === 'TOTAL') {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                    }
                    // Level 1: Cost Activity Row (No index, descText doesn't start with spaces)
                    else if (rawRow[0] === '' && descText && !descText.startsWith('   ')) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [230, 230, 230]; // Darker gray
                    }
                    // Level 2: Cost Center Row (No index, descText starts with 3 spaces)
                    else if (rawRow[0] === '' && descText && descText.startsWith('   ')) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [245, 245, 245]; // Medium gray
                    }
                }
            }
        }
    });

    let finalY = (doc as any).lastAutoTable.finalY || 52;

    if (muatSummaryBody.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('RINGKASAN TOTAL PEMUAT (MUAT TBS)', margins.left, finalY + 10);
        
        autoTable(doc, {
            startY: finalY + 12,
            margin: { top: 52, bottom: 20, left: margins.left },
            head: [
                ['NO', 'NAMA PEMUAT', 'TOTAL JANJANG', 'TOTAL BERAT (KG)', 'TOTAL HK', 'TOTAL PREMI']
            ],
            body: muatSummaryBody,
            theme: 'grid',
            styles: {
                fontSize: 7.5,
                cellPadding: 1.5,
                halign: 'center',
                valign: 'middle',
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                textColor: [0, 0, 0]
            },
            headStyles: {
                fillColor: [245, 245, 245],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                lineWidth: 0.1,
                fontSize: 8,
                cellPadding: 1.5
            },
            columnStyles: {
                0: { halign: 'right', cellWidth: 10 },
                1: { halign: 'left', cellWidth: 70 },
                2: { halign: 'right', cellWidth: 25 },
                3: { halign: 'right', cellWidth: 28 },
                4: { halign: 'right', cellWidth: 28 },
                5: { halign: 'right', cellWidth: 29 }
            },
            didParseCell: (data) => {
                if (data.row.section === 'body') {
                    const rawRow = data.row.raw as any;
                    if (rawRow && rawRow[1] && (rawRow[1] === 'TOTAL' || rawRow[1].content === 'TOTAL')) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                    }
                }
            }
        });
        finalY = (doc as any).lastAutoTable.finalY || finalY;
    }

    if (langsirSummaryBody.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('RINGKASAN TOTAL PELANGSIR (LANGSIR TBS)', margins.left, finalY + 10);
        
        autoTable(doc, {
            startY: finalY + 12,
            margin: { top: 52, bottom: 20, left: margins.left },
            head: [
                ['NO', 'NAMA PELANGSIR', 'TOTAL JANJANG', 'TOTAL BERAT (KG)', 'TOTAL HK', 'TOTAL PREMI']
            ],
            body: langsirSummaryBody,
            theme: 'grid',
            styles: {
                fontSize: 7.5,
                cellPadding: 1.5,
                halign: 'center',
                valign: 'middle',
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                textColor: [0, 0, 0]
            },
            headStyles: {
                fillColor: [245, 245, 245],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                lineWidth: 0.1,
                fontSize: 8,
                cellPadding: 1.5
            },
            columnStyles: {
                0: { halign: 'right', cellWidth: 10 },
                1: { halign: 'left', cellWidth: 70 },
                2: { halign: 'right', cellWidth: 25 },
                3: { halign: 'right', cellWidth: 28 },
                4: { halign: 'right', cellWidth: 28 },
                5: { halign: 'right', cellWidth: 29 }
            },
            didParseCell: (data) => {
                if (data.row.section === 'body') {
                    const rawRow = data.row.raw as any;
                    if (rawRow && rawRow[1] && (rawRow[1] === 'TOTAL' || rawRow[1].content === 'TOTAL')) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                    }
                }
            }
        });
        finalY = (doc as any).lastAutoTable.finalY || finalY;
    }

    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        drawHeader(doc, params, i, totalPages);
    }

    return doc;
};

export const downloadBkmMuatPDF = (data: BkmMuatRow[], params: BkmMuatParams, rkhBlocks: any[] = []) => {
    const doc = generateBkmMuatPDF(data, params, rkhBlocks);
    const filename = `BKM_Muat_${params.division_name || 'Report'}_${format(parseDateString(params.posting_date), 'yyyyMMdd')}.pdf`;
    doc.save(filename);
    trackDownload('BKM Muat', { filename });
};
